/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   CollectionRunDetail.tsx
 *     Feature         :   139-collection-job-console
 *     Description     :   Accordion panel — step breakdown + error list for a run (US4)
 *     Constitution    :   v1.12.0 | Principle XXXVII (Code Headers)
 *     Author          :   Peter Dobler
 *     Created         :   2026-03-02
 * =========================================================================================
 */

import React, { useState } from 'react';
import { CheckCircle2, AlertTriangle, XCircle, ChevronDown, ChevronUp, Clock, Zap } from 'lucide-react';
import { useFcjlogsRunSteps, useFcjlogsStepEntries } from '../../services/operations/fcjlogsService';
import { useCollectionRunById } from '../../services/operations/collectionJobService';
import { LoadingSpinner } from '../../components/operations/LoadingSpinner';
import type { CollectionRun } from '../../types/collectionJob';
import { formatEastern } from '@/utils/dateFormatter';

interface CollectionRunDetailProps {
  runId: string;
}

/** Format milliseconds to friendly duration */
function formatMs(ms: number | null): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  const s = Math.round(ms / 1000);
  const m = Math.floor(s / 60);
  if (m > 0) return `${m}m ${s % 60}s`;
  return `${s}s`;
}

/** Format seconds to duration */
function formatDuration(seconds: number | null): string {
  if (seconds == null) return '—';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  const h = Math.floor(m / 60);
  if (h > 0) return `${h}h ${m % 60}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

/** Format ISO date */
function formatDate(iso: string): string {
  return formatEastern(iso);
}

/** Step status icon */
function StepIcon({ errorCount, warningCount }: { errorCount: number; warningCount: number }) {
  if (errorCount > 0) return <XCircle className="w-4 h-4 text-red-500" />;
  if (warningCount > 0) return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
  return <CheckCircle2 className="w-4 h-4 text-green-500" />;
}

/** Error entries section for a specific step */
function StepErrors({ runId, stepName }: { runId: string; stepName: string }) {
  const [showAll, setShowAll] = useState(false);
  const { data, isLoading } = useFcjlogsStepEntries(runId, stepName, 'ERROR', 100, 0);

  if (isLoading) return <div className="text-xs text-[var(--color-text-secondary)] pl-8 py-1">Loading errors...</div>;

  const entries = data?.entries ?? [];
  if (entries.length === 0) return null;

  const displayed = showAll ? entries : entries.slice(0, 10);

  return (
    <div className="pl-8 mt-1 space-y-1">
      {displayed.map((entry: any, idx: number) => (
        <div key={entry.id || idx} className="text-xs text-red-600 dark:text-red-400 flex items-start gap-1.5">
          <XCircle className="w-3 h-3 mt-0.5 shrink-0" />
          <span className="wrap-break-word">{entry.message}</span>
        </div>
      ))}
      {entries.length > 10 && !showAll && (
        <button
          onClick={() => setShowAll(true)}
          className="text-xs text-[var(--color-accent)] hover:underline pl-4"
        >
          Show all {entries.length} errors
        </button>
      )}
    </div>
  );
}

export const CollectionRunDetail: React.FC<CollectionRunDetailProps> = ({ runId }) => {
  const [expandedStep, setExpandedStep] = useState<string | null>(null);
  const { data: run, isLoading: runLoading, error: runError } = useCollectionRunById(runId);
  const { data: stepsData, isLoading: stepsLoading, error: stepsError } = useFcjlogsRunSteps(runId);

  if (runLoading || stepsLoading) return <LoadingSpinner size="sm" message="Loading run details..." />;

  if (runError || stepsError) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 border border-red-200 dark:border-red-700 mt-1">
        <p className="text-sm text-red-600 dark:text-red-400">
          Failed to load run details: {(runError as Error)?.message || (stepsError as Error)?.message || 'Unknown error'}
        </p>
      </div>
    );
  }

  const steps = stepsData?.steps ?? [];

  return (
    <div className="bg-[var(--color-bg-subtle)] rounded-lg p-4 border border-[var(--color-border)] mt-1">
      {/* Run header */}
      {run && (
        <div className="mb-4 pb-3 border-b border-[var(--color-border)]">
          <div className="flex items-center justify-between mb-1">
            <h4 className="text-sm font-semibold text-[var(--color-text-primary)] font-mono">
              {run.id.substring(0, 8)}…
            </h4>
            <span className="text-xs text-[var(--color-text-secondary)]">
              {formatDuration(run.duration_seconds)}
            </span>
          </div>
          <p className="text-xs text-[var(--color-text-secondary)]">
            {formatDate(run.started_at)}
            {run.completed_at && ` → ${formatDate(run.completed_at)}`}
          </p>
          {run.error_message && (
            <p className="mt-2 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded p-2">
              {run.error_message}
            </p>
          )}

          {/* Entity breakdown */}
          <div className="mt-2 grid grid-cols-3 gap-2 text-xs text-[var(--color-text-secondary)]">
            <div>Capacities: {run.capacities_processed}</div>
            <div>Workspaces: {run.workspace_snapshots_collected}</div>
            <div>Lakehouses: {run.lakehouse_snapshots_collected}</div>
            <div>Warehouses: {run.warehouse_snapshots_collected}</div>
            <div>Pipelines: {run.pipeline_snapshots_collected}</div>
            <div>Notebooks: {run.notebook_snapshots_collected}</div>
            <div>Reports: {run.report_snapshots_collected}</div>
            <div>Sem. Models: {run.semantic_model_snapshots_collected}</div>
            <div>Storage Accts: {run.storage_account_snapshots_collected}</div>
          </div>
        </div>
      )}

      {/* Step list */}
      {steps.length === 0 ? (
        <p className="text-xs text-[var(--color-text-secondary)] text-center py-4">
          No step data available for this run
        </p>
      ) : (
        <div className="space-y-1">
          <h5 className="text-xs font-semibold text-[var(--color-text-secondary)] mb-2 uppercase tracking-wider">
            Steps
          </h5>
          {steps.map((step: any) => (
            <div key={step.step_name}>
              <button
                onClick={() => setExpandedStep(expandedStep === step.step_name ? null : step.step_name)}
                className="w-full flex items-center gap-2 py-1.5 px-2 rounded hover:bg-[var(--color-bg-subtle)] transition-colors text-left"
              >
                <StepIcon errorCount={step.error_count ?? 0} warningCount={step.warning_count ?? 0} />
                <span className="text-sm text-[var(--color-text-secondary)] flex-1 truncate">
                  {step.step_name}
                </span>
                <div className="flex items-center gap-3 text-xs text-[var(--color-text-secondary)]">
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {formatMs(step.duration_ms)}
                  </span>
                  <span className="flex items-center gap-1">
                    <Zap className="w-3 h-3" />
                    {step.entities_processed ?? 0}
                  </span>
                  {expandedStep === step.step_name
                    ? <ChevronUp className="w-3.5 h-3.5" />
                    : <ChevronDown className="w-3.5 h-3.5" />}
                </div>
              </button>

              {/* Error entries (expanded) */}
              {expandedStep === step.step_name && (step.error_count > 0 || step.critical_count > 0) && (
                <StepErrors runId={runId} stepName={step.step_name} />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
